import { VdTracking } from '@vend/vend.js'
import { IScope } from 'angular'

import { STATES } from '../../../../lib/routes'
import { EVENT, PERMISSIONS } from '../../constants'
import { CashMovementsManager } from '../../managers/cash-movements-manager'
import { RegisterClosureManager } from '../../managers/register-closure-manager'
import { RegisterManager } from '../../managers/register-manager'
import { UserManager } from '../../managers/user-manager'
import { $rootScope, $state } from '../../ng-compat/imports'
import { CallToAction } from './types'

/**
 * Controller for the open register view where a user can view the previous register closure summary and open the
 * register.
 *
 * @class OpenRegisterController
 */
export class OpenRegisterController {
  public CLOSE_REGISTER_STATE = STATES.CLOSE_REGISTER
  public isCashManagementEnabled: boolean

  /**
   * Identify if the register is open.
   */
  public isRegisterOpen: boolean

  public registerClosure: $TSFixMe
  public registerClosureSalesUrl: $TSFixMe
  public callToAction: CallToAction

  constructor(
    $scope: IScope,
    private registerManager: RegisterManager,
    private registerClosureManager: RegisterClosureManager,
    private userManager: UserManager,
    cashMovementsManager: CashMovementsManager,
    private vdTracking: VdTracking
  ) {
    'ngInject'
    this.isCashManagementEnabled = cashMovementsManager.isCashManagementEnabled()

    this.callToAction = {
      copy: 'Switch User',
      href: '/signout?return=' + window.location.pathname,
    }

    this.isRegisterOpen = registerManager.isActiveRegisterOpen()

    this._getLastRegisterClosureSummary()

    registerManager.on('registerIdChange', this._evaluateRegisterState, this)

    // The register closure state resolver will default to the "open" register state (see routes-register-closure). In
    // the instance where an active register has not been selected, listen for the registerChange event
    if (!registerManager.getActiveRegister()) {
      registerManager.once(
        'activeRegisterChange',
        this._evaluateRegisterState,
        this
      )
    }

    $scope.$on('$destroy', () => {
      this.registerManager.off('registerIdChange', this._evaluateRegisterState)
      this.registerManager.off(
        'activeRegisterChange',
        this._evaluateRegisterState
      )
    })
  }

  /**
   * Fires a tracking event to track visits to register closure sumamry sales.
   *
   * @method registerSaleTracking
   */
  public registerSaleTracking() {
    this.vdTracking.trackUserEvent({
      eventName: 'Register Closure Summary Visit',
    })
  }

  public hasViewReportingRegisterClosurePermission() {
    return this.userManager.hasPermission(
      PERMISSIONS.REPORTING_REGISTER_CLOSURE
    )
  }

  /**
   * Print the register closure summary screen.
   *
   * @method printSummary
   */
  public printSummary() {
    $rootScope.$emit(EVENT.PRINT, {
      type: 'register-closure',
      data: this.registerClosureManager.generateRegisterClosurePrintData(
        this.registerClosure
      ),
    })
  }

  /**
   * Check whether current user has the permission to open or close a register
   *
   * @method hasOpenClosePermission
   */

  public hasOpenClosePermission() {
    return this.registerClosureManager.hasOpenClosePermission()
  }

  /**
   * Handler for when either the register id changes (register is switched) or when a register is selected for the
   * first time.
   *
   * @private
   * @method _evaluateRegisterState
   */
  private _evaluateRegisterState() {
    this.isRegisterOpen = this.registerManager.isActiveRegisterOpen()
    if (this.isRegisterOpen) {
      $state.go(this.CLOSE_REGISTER_STATE)
    } else {
      this._getLastRegisterClosureSummary()
    }
  }

  /**
   * Set the register closure url.
   *
   * @private
   * @method _setRegisterClosureSalesUrl
   */
  private _setRegisterClosureSalesUrl(registerClosure: $TSFixMe) {
    let registerClosureSalesUrl

    if (registerClosure) {
      const id = registerClosure.register_closure_id

      registerClosureSalesUrl = `/register/closure/summary/${id}`
    }

    this.registerClosureSalesUrl = registerClosureSalesUrl
  }

  /**
   * Retrieve the last register closure summary.
   *
   * @private
   * @method _getLastRegisterClosureSummary
   */
  private _getLastRegisterClosureSummary() {
    this.registerClosureManager
      .getLastRegisterClosureSummary()
      .then((registerClosure: $TSFixMe) => {
        this.registerClosure = registerClosure

        this._setRegisterClosureSalesUrl(registerClosure)
      })
  }
}
